import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import Sidebar from "../../CommonComponent/Sidebar";
import CommonHeader from "../../CommonComponent/CommonHeader";
import CustomBreadcrumb from "../../CommonComponent/CommonBreadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import GeneralSettings from "./GeneralSettings";
import CompanySettings from "./CompanySettings";
import PolicySettings from "./PolicySettings";
import { getAllFrameworks } from "../../api/bannerService";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../toastUtils";
import {
  generatePrivacyPolicy,
  getPrivacyPolicyId,
  updatePrivacyPolicy,
} from "../../api/privacyPolicy";
import { getWebsiteById } from "../../api/websiteService";
import Loader from "../../CommonComponent/Loader";

const CreatePrivacyPolicy = () => {
  // Define sidebar tabs
  const sidebarTabs = [
    "General Settings",
    "Company Settings",
    "Policy Settings",
  ];

  // State for active tab
  const [activeTab, setActiveTab] = useState(sidebarTabs[0]);
  const inputRef = useRef(null); // Use ref to directly control the input element
  const navigate = useNavigate();

  // State for API data
  const [frameworks, setFrameworks] = useState([]);
  console.log("frameworks", frameworks)
  const [isFrameworksLoading, setIsFrameworksLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [initialFormData, setInitialFormData] = useState({});
  const [formData, setFormData] = useState({
    // Form 1 - Policy & Language
    policy_name: "",
    framework_id: "", // Using GDPR framework as default as per your example
    language: "",
    hosting_preference: "dpc",

    // Form 2 - Company Information
    config: {
      introduction: {
        company_name: "",
        domain_url: "",
        dpo_email: "",
        dpo_phone: "",
      },

      // Form 3 - Data Collection & Protection
      personal_data: {
        personal_data_types: [],
      },
      data_collection_purpose: {
        data_purposes: [],
      },
      data_sharing: {
        data_sharing: {
          yes: false,
        },
      },
      user_rights: {
        response_days: 30,
      },
      data_storage_and_protection: {
        data_storage: {
          locations: [],
          international_data_transfer: false,
        },
        data_protection_measures: {},
      },
      cookies_tracking: {
        cookie_policy_url: "",
      },
      childrens_privacy: {
        yes: false,
      },
      marketing_and_communication: {
        direct_marketing: false,
        marketing_channels: [],
      },
      breach_notification: {
        breach_notification_channels: [],
      },
      contact_us: {
        contact_email: "",
        contact_phone: "",
      },
    },
  });

  // Fetch languages and frameworks on component mount
  useEffect(() => {
    fetchFrameworks();
  }, []);

  // In CreatePrivacyPolicy.js
  const fetchFrameworks = async () => {
    setIsFrameworksLoading(true);
    try {
      // Replace with your actual API endpoint
      const response = await getAllFrameworks();
      // Use the actual structure from your API response
      if (response.success && response.data && response.data.consent_data) {
        setFrameworks(response.data.consent_data);
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      console.error("Error fetching frameworks:", error);
    } finally {
      setIsFrameworksLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Store the selection position
    const selectionStart = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;

    // Handle nested properties
    if (name.includes(".")) {
      const parts = name.split(".");
      setFormData((prevState) => {
        let newState = { ...prevState };
        let current = newState;

        // Navigate to the nested property
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }

        // Set the value
        current[parts[parts.length - 1]] = value;
        return newState;
      });
    } else {
      // Handle top-level properties
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }

    // Restore the cursor position and focus after state update
    if (inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(selectionStart, selectionEnd);
        }
      }, 0);
    }

    if (formTouched[activeTab]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle boolean input changes with improved focus retention
  const handleBooleanChange = (name, value) => {
    // Store active element
    const activeElement = document.activeElement;
    const activeId = activeElement ? activeElement.id : null;

    // Handle nested properties
    if (name.includes(".")) {
      const parts = name.split(".");
      setFormData((prevState) => {
        let newState = { ...prevState };
        let current = newState;

        // Navigate to the nested property
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }

        // Set the value
        current[parts[parts.length - 1]] = value;
        return newState;
      });
    } else {
      // Handle top-level properties
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }

    // Restore focus if needed
    setTimeout(() => {
      if (activeId) {
        const element = document.getElementById(activeId);
        if (element) element.focus();
      }
    }, 0);
  };

  // Handle array input changes (checkboxes for multi-select) with improved focus handling
  const handleArrayInputChange = (name, value, isChecked) => {
    // Store active element
    const activeElement = document.activeElement;
    const activeId = activeElement ? activeElement.id : null;

    // Handle nested properties
    if (name.includes(".")) {
      const parts = name.split(".");
      setFormData((prevState) => {
        let newState = { ...prevState };
        let current = newState;

        // Navigate to the nested property
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }

        // Get the current array
        const currentArray = current[parts[parts.length - 1]] || [];

        // Update the array
        if (isChecked) {
          current[parts[parts.length - 1]] = [...currentArray, value];
        } else {
          current[parts[parts.length - 1]] = currentArray.filter(
            (item) => item !== value
          );
        }

        return newState;
      });
    }

    // Restore focus if needed
    setTimeout(() => {
      if (activeId) {
        const element = document.getElementById(activeId);
        if (element) element.focus();
      }
    }, 0);
  };

  // Validate form when changing tabs or on mount
  useEffect(() => {
    checkFormValidity(activeTab);
    //eslint-disable-next-line
  }, [activeTab]);

  const location = useLocation();
  const { privacyId, isEditing } = location.state || {};

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      if (isEditing) {
        const {
          html_content,
          legal_framework,
          policy_id,
          ...formDataWithoutHtml
        } = formData;
        const res = await updatePrivacyPolicy(privacyId, formDataWithoutHtml);
        if (res.status_code === 200) {
          setLoading(false);
          navigate("/privacy-policy");
          showSuccessToast(res.message);
        }
      } else {
        const websiteId = localStorage.getItem("website_id"); // Replace with the actual website ID (can be dynamic)
        const websiteData = await getWebsiteById(websiteId);
        const response = await generatePrivacyPolicy(
          formData,
          websiteData?.data?.account_hashid
        );
        if (response?.status_code === 201) {
          navigate("/privacy-policy");
          setLoading(false);
        } else {
          showErrorToast(response?.message);
          setLoading(false);
        }
      }
    } catch (error) {
      showErrorToast("Error submitting privacy policy:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isEditing && privacyId) {
        setLoading(true);
        try {
          // 👇 Replace with your real API call
          const res = await getPrivacyPolicyId(privacyId);

          if (res?.data) {
            setFormData(res.data); // Make sure this matches your form's structure
            // setInitialFormData(res.data);

            // Check if DPO info exists
            const { dpo_email, dpo_phone } =
              res.data?.config?.introduction || {};
            setHasDpo(Boolean(dpo_email && dpo_phone)); // Simplified check
          } else {
            console.error("No data returned from API");
          }
        } catch (err) {
          console.error("Error fetching data:", err);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData(); // Call the async function
  }, [isEditing, privacyId]);

  // Add this to your existing state
  const [errors, setErrors] = useState({});
  const [formTouched, setFormTouched] = useState({
    "General Settings": false,
    "Company Settings": false,
    "Policy Settings": false,
  });
  const [hasDpo, setHasDpo] = useState(false);

  // Function to validate General Settings
  const validateGeneralSettings = (data) => {
    let formErrors = {};

    if (!data.policy_name?.trim())
      formErrors.policy_name = "Policy name is required";
    if (!data.framework_id)
      formErrors.framework_id = "Framework selection is required";
    if (!data.language) formErrors.language = "Language selection is required";
    // if (!data.hosting_preference)
    //   formErrors.hosting_preference = "Hosting preference is required";

    return formErrors;
  };

  // Function to validate Company Settings
  const validateCompanySettings = (data) => {
    let formErrors = {};

    // Company name validation
    if (!data.config?.introduction?.company_name?.trim())
      formErrors["config.introduction.company_name"] =
        "Company name is required";

    const domainRegex = /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/)?$/;

    if (!data.config?.introduction?.domain_url?.trim()) {
      formErrors["config.introduction.domain_url"] = "Domain URL is required";
    } else if (!domainRegex.test(data.config.introduction.domain_url.trim())) {
      formErrors["config.introduction.domain_url"] =
        "Enter a valid domain (e.g. https://example.com or http://example.com)";
    }


    // Contact email validation
    if (!data.config?.contact_us?.contact_email?.trim()) {
      formErrors["config.contact_us.contact_email"] =
        "Contact email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(data.config?.contact_us?.contact_email)) {
      formErrors["config.contact_us.contact_email"] =
        "Please enter a valid email address";
    }

    // Contact phone validation
    if (!data.config?.contact_us?.contact_phone?.trim())
      formErrors["config.contact_us.contact_phone"] =
        "Contact phone is required";

    // DPO validations - only required if hasDpo is true
    if (hasDpo) {
      if (!data.config?.introduction?.dpo_email?.trim()) {
        formErrors["config.introduction.dpo_email"] = "DPO email is required";
      } else if (!/^\S+@\S+\.\S+$/.test(data.config?.introduction?.dpo_email)) {
        formErrors["config.introduction.dpo_email"] =
          "Please enter a valid email address";
      }

      if (!data.config?.introduction?.dpo_phone?.trim())
        formErrors["config.introduction.dpo_phone"] = "DPO phone is required";
    }

    return formErrors;
  };

  const validatePolicySettings = (data) => {
    let formErrors = {};

    // 1. Personal Data Types
    if (!data.config?.personal_data?.personal_data_types?.length) {
      formErrors["config.personal_data.personal_data_types"] =
        "At least one personal data type is required";
    }

    // 2. Data Collection Purposes
    if (!data.config?.data_collection_purpose?.data_purposes?.length) {
      formErrors["config.data_collection_purpose.data_purposes"] =
        "At least one data purpose is required";
    }

    // 3. Data Storage Locations
    if (
      !data.config?.data_storage_and_protection?.data_storage?.locations?.length
    ) {
      formErrors["config.data_storage_and_protection.data_storage.locations"] =
        "At least one storage location is required";
    }

    // 4. International Data Transfer
    if (
      typeof data.config?.data_storage_and_protection?.data_storage
        ?.international_data_transfer !== "boolean"
    ) {
      formErrors[
        "config.data_storage_and_protection.data_storage.international_data_transfer"
      ] = "Please specify if international data transfer occurs";
    }

    // 5. Data Protection Measures
    if (
      !Object.keys(
        data.config?.data_storage_and_protection?.data_protection_measures || {}
      ).length
    ) {
      formErrors[
        "config.data_storage_and_protection.data_protection_measures"
      ] = "At least one data protection measure is required";
    }

    // 6. Data Sharing
    if (typeof data.config?.data_sharing?.data_sharing?.yes !== "boolean") {
      formErrors["config.data_sharing.data_sharing.yes"] =
        "Please specify if data is shared with third parties";
    }

    // 7. Cookie Policy URL
    // const cookiePolicyUrl =
    //   data.config?.cookies_tracking?.cookie_policy_url?.trim();
    // if (!cookiePolicyUrl) {
    //   formErrors["config.cookies_tracking.cookie_policy_url"] =
    //     "Cookie Policy URL is required";
    // } else if (!/^https?:\/\/.+\..+/.test(cookiePolicyUrl)) {
    //   formErrors["config.cookies_tracking.cookie_policy_url"] =
    //     "Please enter a valid URL";
    // }

    // 8. Children's Privacy
    if (typeof data.config?.childrens_privacy?.yes !== "boolean") {
      formErrors["config.childrens_privacy.yes"] =
        "Please specify if your website serves children";
    }

    // 9. Marketing & Communication - Direct Marketing
    if (
      typeof data.config?.marketing_and_communication?.direct_marketing !==
      "boolean"
    ) {
      formErrors["config.marketing_and_communication.direct_marketing"] =
        "Please specify if you use direct marketing";
    }

    // 10. Marketing Channels (only if direct marketing is true)
    if (
      data.config?.marketing_and_communication?.direct_marketing &&
      !data.config?.marketing_and_communication?.marketing_channels?.length
    ) {
      formErrors["config.marketing_and_communication.marketing_channels"] =
        "At least one marketing channel is required";
    }

    // 11. Breach Notification Channels
    if (
      !data.config?.breach_notification?.breach_notification_channels?.length
    ) {
      formErrors["config.breach_notification.breach_notification_channels"] =
        "At least one breach notification method is required";
    }

    // 12. User Rights Response Days
    const responseDays = data.config?.user_rights?.response_days;
    if (
      typeof responseDays !== "number" ||
      responseDays < 1 ||
      responseDays > 60
    ) {
      formErrors["config.user_rights.response_days"] =
        "Please select a valid response time between 1 and 60 days";
    }

    return formErrors;
  };

  // Create a function to check if an entire form is valid
  const checkFormValidity = (formName) => {
    let validationErrors = {};

    switch (formName) {
      case "General Settings":
        validationErrors = validateGeneralSettings(formData);
        break;
      case "Company Settings":
        validationErrors = validateCompanySettings(formData);
        break;
      case "Policy Settings":
        validationErrors = validatePolicySettings(formData);
        break;
      default:
        break;
    }

    // Update errors state with new errors
    setErrors(validationErrors);

    // Return whether the form is valid (no errors)
    return Object.keys(validationErrors).length === 0;
  };

  // const isDeepEqual = (obj1, obj2) => {
  //   if (obj1 === obj2) return true;

  //   if (
  //     typeof obj1 !== "object" ||
  //     typeof obj2 !== "object" ||
  //     obj1 === null ||
  //     obj2 === null
  //   ) {
  //     return false;
  //   }

  //   const keys1 = Object.keys(obj1);
  //   const keys2 = Object.keys(obj2);
  //   if (keys1.length !== keys2.length) return false;

  //   for (const key of keys1) {
  //     if (!keys2.includes(key)) return false;
  //     if (!isDeepEqual(obj1[key], obj2[key])) return false;
  //   }

  //   return true;
  // };

  // const hasFormChanged = () => !isDeepEqual(initialFormData, formData);

  const renderContent = () => {
    switch (activeTab) {
      case "General Settings":
        return (
          <GeneralSettings
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
            frameworks={frameworks}
            isFrameworksLoading={isFrameworksLoading}
            inputRef={inputRef}
            onNext={() => {

              setFormTouched((prev) => ({ ...prev, "General Settings": true }));
              if (checkFormValidity("General Settings")) {
                setActiveTab(sidebarTabs[1]);
              }
            }}
            errors={formTouched["General Settings"] ? errors : {}}
            isEditing={isEditing}
          />
        );
      case "Company Settings":
        return (
          <CompanySettings
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
            inputRef={inputRef}
            onNext={() => {
              setFormTouched((prev) => ({ ...prev, "Company Settings": true }));

              const domain = formData?.config?.introduction?.domain_url?.trim();

              if (!domain || (!domain.startsWith("https://") && !domain.startsWith("http://"))) {
                setErrors((prev) => ({
                  ...prev,
                  "config.introduction.domain_url": "URL must start with http:// or https://",
                }));
                return;
              }

              if (checkFormValidity("Company Settings")) {
                setActiveTab(sidebarTabs[2]);
              }
            }}

            onBack={() => setActiveTab(sidebarTabs[0])}
            errors={formTouched["Company Settings"] ? errors : {}}
            hasDpo={hasDpo}
            setHasDpo={setHasDpo}
            setErrors={setErrors}
          />
        );
      case "Policy Settings":
        return (
          <PolicySettings
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
            handleArrayInputChange={handleArrayInputChange}
            onSubmit={(e) => {
              e.preventDefault(); // prevent page reload
              setFormTouched((prev) => ({ ...prev, "Policy Settings": true }));
              if (checkFormValidity("Policy Settings")) {
                handleSubmit();
              }
            }}
            inputRef={inputRef}
            onBack={() => setActiveTab(sidebarTabs[1])}
            loading={loading}
            errors={formTouched["Policy Settings"] ? errors : {}}
          // isSubmitDisabled={!hasFormChanged()}
          />
        );
      default:
        return <GeneralSettings />;
    }
  };

  const breadcrumbItems = [
    {
      text: "Privacy Policy",
      href: "/privacy-policy",
    },
    {
      text: "Generate Privacy Policy",
    },
  ];

  const handleTabChange = (nextTab) => {
    const currentTab = activeTab;
    const isValid = checkFormValidity(currentTab);
    if (isValid) {
      setFormTouched((prev) => ({ ...prev, [currentTab]: true }));
      setActiveTab(nextTab); // allow switching
    } else {
      setFormTouched((prev) => ({ ...prev, [currentTab]: true }));
      showInfoToast("Please fill all required fields before switching tabs.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 3 }}>
        <CommonHeader
          title="Generate Privacy Policy"
          subTitle="Here you can fill the forms and generate privacy policy."
        />
      </Box>
      <Box sx={{ mb: 4 }}>
        <CustomBreadcrumb
          items={breadcrumbItems}
          onBackClick={() => navigate("/privacy-policy")}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          height: "calc(100% - 64px)",
          position: "relative",
          "@media (max-width: 768px)": {
            // For mobile devices
            flexDirection: "column",
          },
          mt: 3,
        }}
      >
        <Box
          sx={{
            width: "250px",
            borderRight: "1px solid #e0e0e0",
            bgcolor: "white",
            flexShrink: 0,
            "@media (max-width: 768px)": {
              width: "100%",
              borderRight: "none",
              borderBottom: "1px solid #e0e0e0",
            },
          }}
        >
          <Sidebar
            tabs={sidebarTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            overflowY: "auto",
            maxWidth: "800px", // Maximum width for content
            margin: "0 auto", // Center content on larger screens
            width: "100%",
            "@media (max-width: 768px)": {
              maxWidth: "100%",
              padding: 2,
            },
          }}
        >
          {loading && isEditing ? <Loader /> : renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default CreatePrivacyPolicy;
